import { getValue } from './transform';

/**
 * Who is calling, what they are calling, and whose mapping should run.
 *
 * A vendor posts to a shared vanity URL with an api-key and nothing else. Three questions have
 * to be answered before a payload can be transformed, and none of them may cost a database
 * query on the request path:
 *
 *   1. which business and tenant does this key belong to  → the credential
 *   2. which operation is this                            → the route binding
 *   3. whose mapping should run                           → the vendor, then the scope
 *
 * Everything here is data a person authors in the console. Adding an endpoint, a vendor or a
 * rule is a publication, never a deployment.
 */

/**
 * A unit that holds its own credential. One tenant may have several, each with its own key,
 * which is what lets two businesses under one tenant be told apart at the door.
 */
export interface Business {
  id: string;
  name: string;
  tenantId: string;
  /** How the vendor addresses this business, when its own id differs from ours. */
  externalUnitId?: string;
  pos?: string;
}

/**
 * An api-key, stored only as a hash. Holding the key itself would put a live credential in the
 * configuration document, the audit log and every backup of them.
 */
export interface Credential {
  id: string;
  keyHash: string;
  /** The one business this key speaks for. Empty when the key is the tenant's and the request names the unit. */
  businessId: string;
  /** Set for a key issued to a whole tenant, which may then reach any unit under that tenant and no other. */
  tenantId?: string;
  label: string;
  /** Set when the integration is known to be one vendor, which skips discrimination entirely. */
  vendorId?: string;
  /** Empty means every route configured for the business. */
  allowedRouteIds?: string[];
  /** `directory` means this key came from the system that owns it, so re-reading may retire it. */
  origin?: 'directory';
  createdAt: string;
  revokedAt?: string;
}

/** A public path, mapped to the operation it performs. */
/**
 * How a caller proves who they are on one endpoint.
 *
 * Vendors do not agree on this. One sends `api-key`. One sends a token under its own header
 * beside a tenant header, with the unit in the URL. None of that is worth a code change, so the
 * header names and the place the unit is named are authored here and nothing is assumed. The
 * secret is still only ever compared as a hash, whatever header carried it.
 */
export interface AuthScheme {
  id: string;
  name: string;
  /** The header carrying the secret. */
  keyHeader: string;
  /**
   * Where the calling unit is named, for a key that is shared across units. Left out, the key
   * itself says which business it belongs to, which is the usual case.
   *
   * `matches` says which of our two names for a unit the value is: our own id, or the id the
   * vendor knows it by.
   */
  unitFrom?: { source: 'path' | 'header'; name: string; matches: 'id' | 'externalUnitId' };
  /** A header the vendor sends naming the tenant. Checked against the key, never trusted alone. */
  tenantHeader?: string;
  /** Read when `keyHeader` is absent, for vendors that send the same secret under another name. */
  fallbackKeyHeader?: string;
  /** The status a refusal answers with, so vendors keep the codes they already branch on. 403 when unset. */
  refusalStatus?: Partial<Record<RefusalKind, number>>;
}

export const REFUSAL_KINDS = ['missingKey', 'wrongKey', 'wrongTenant', 'wrongUnit'] as const;
export type RefusalKind = (typeof REFUSAL_KINDS)[number];

/** What is used when a binding names no scheme: one header, and the key names the unit. */
export const DEFAULT_AUTH_SCHEME: AuthScheme = { id: 'default', name: 'API key', keyHeader: 'api-key' };

/** A public path, mapped to the operation it performs. */
export interface RouteBinding {
  id: string;
  method: 'POST' | 'PUT' | 'GET';
  path: string;
  routeId: string;
  /** Which authored scheme guards this path. Absent means {@link DEFAULT_AUTH_SCHEME}. */
  authSchemeId?: string;
  /** The Casa service that answers this endpoint straight away, instead of it being queued. */
  upstream?: string;
  /** The vendor sends XML, which the door reads as JSON. */
  format?: 'xml';
  /** One request carries a list of bills. */
  batch?: boolean;
  /**
   * The vendor's name in front of the docs path (`/logicerp/v2/bills`). The data plane takes it
   * off again before a request is handed to Casa's own backend, which knows only the docs path.
   */
  prefix?: string;
}

/** One test against the request. Every clause of a rule must hold for the rule to match. */
export type DiscriminatorClause =
  | { kind: 'field-equals'; path: string; value: string }
  | { kind: 'field-present'; path: string }
  | { kind: 'field-absent'; path: string }
  | { kind: 'header-contains'; header: string; value: string };

/**
 * Which vendor sent this, when the key alone cannot say. Rules are tried in priority order and
 * the first match wins; a rule with no clauses matches anything and so acts as the default.
 */
export interface VendorRule {
  id: string;
  routeId: string;
  vendorId: string;
  /** Narrows the rule to one business, or one tenant. Absent means it applies everywhere. */
  businessId?: string;
  tenantId?: string;
  priority: number;
  clauses: DiscriminatorClause[];
}

export interface IdentityState {
  businesses?: Business[];
  credentials?: Credential[];
  routeBindings?: RouteBinding[];
  authSchemes?: AuthScheme[];
  vendorRules?: VendorRule[];
  /** First path segments another service already answers on; no binding may start with one. */
  reservedPrefixes?: string[];
}

/** What a request resolved to, or why it could not. */
export type Resolution =
  | {
      ok: true;
      businessId: string;
      tenantId: string;
      routeId: string;
      vendorId: string;
      scope: string;
      why: string;
      /** Whatever the path captured, such as the unit id out of the URL. Empty for a plain path. */
      params: Record<string, string>;
    }
  | {
      ok: false;
      reason:
        | 'unknown-key'
        | 'revoked-key'
        | 'unknown-path'
        | 'route-not-allowed'
        | 'unknown-unit'
        | 'unit-not-allowed'
        | 'tenant-mismatch'
        | 'no-scheme';
      detail: string;
    }
  | { ok: false; reason: 'no-vendor'; detail: string; routeId: string }
  | { ok: false; reason: 'ambiguous-vendor'; detail: string; routeId: string; candidates: string[] };

export const findCredential = (state: IdentityState, keyHash: string): Credential | undefined =>
  state.credentials?.find((credential) => credential.keyHash === keyHash);

export const businessOf = (state: IdentityState, businessId: string): Business | undefined =>
  state.businesses?.find((business) => business.id === businessId);

/**
 * A path is matched segment by segment. A segment written `{name}` stands for whatever the
 * caller put there and hands the value back under that name, which is how an endpoint whose
 * unit lives in the URL — `/bills/businessUnit/{businessUnitId}/batch` — is configured once
 * and still answers every unit.
 */
const segmentsOf = (path: string): string[] => path.split('/').filter(Boolean);

const parameterName = (segment: string): string | undefined =>
  segment.length > 2 && segment.startsWith('{') && segment.endsWith('}') ? segment.slice(1, -1) : undefined;

const countParameters = (path: string): number =>
  segmentsOf(path).filter((segment) => parameterName(segment) !== undefined).length;

/** The values a path took from the request, empty when the binding captures nothing. */
export interface RouteMatch {
  binding: RouteBinding;
  params: Record<string, string>;
}

const matchPath = (template: string, path: string): Record<string, string> | undefined => {
  const want = segmentsOf(template);
  const got = segmentsOf(path);

  // A plain path still stands for everything beneath it, as it always has. One that captures
  // cannot, because with a spare segment there would be no saying which value fed which name.
  const captures = countParameters(template) > 0;
  if (captures ? got.length !== want.length : got.length < want.length) return undefined;

  const params: Record<string, string> = {};
  for (let index = 0; index < want.length; index += 1) {
    const name = parameterName(want[index]);
    if (name === undefined) {
      if (want[index] !== got[index]) return undefined;
      continue;
    }
    if (!got[index]) return undefined;
    try {
      params[name] = decodeURIComponent(got[index]);
    } catch {
      params[name] = got[index];
    }
  }
  return params;
};

/**
 * Why this path cannot be published, in the author's words, or nothing when it is fine.
 *
 * A half-written `{name}` is the fault worth catching here: it binds without complaint and then
 * silently never matches, which reads as "the vendor's request never arrived".
 */
export const pathTemplateFault = (path: string): string | undefined => {
  if (!path.startsWith('/')) return 'The path must start with /.';

  const [first] = segmentsOf(path);
  if (first === undefined || /[{}]/.test(first)) {
    return 'The first part of the path must be a fixed word, not a {placeholder}.';
  }

  const seen = new Set<string>();
  for (const segment of segmentsOf(path)) {
    const opens = (segment.match(/[{]/g) ?? []).length;
    const closes = (segment.match(/[}]/g) ?? []).length;
    if (opens === 0 && closes === 0) continue;
    const name = parameterName(segment);
    if (name === undefined || opens > 1 || closes > 1) {
      return `"${segment}" is not a whole placeholder. Write one name in braces, such as {businessUnitId}.`;
    }
    if (!/^[A-Za-z][A-Za-z0-9_-]*$/.test(name)) {
      return `"{${name}}" is not a usable name. Start with a letter and use letters, digits, - or _.`;
    }
    if (seen.has(name)) return `"{${name}}" appears twice. Each placeholder needs its own name.`;
    seen.add(name);
  }
  return undefined;
};

/**
 * The data plane's own addresses. On a domain that sends everything to Camel, a vendor path
 * starting with one of these would land on Camel's test runner, its runtime API or its health
 * checks instead of the vendor's endpoint, so no endpoint may take them.
 */
export const PLANE_OWN_PREFIXES = ['test', 'runtime', 'q', 'ingest'];

/** Why this path belongs to another service, or nothing when it is free. */
export const reservedPathFault = (path: string, state: IdentityState): string | undefined => {
  const segments = segmentsOf(path);
  const own = PLANE_OWN_PREFIXES.find((prefix) => segments[0]?.toLowerCase() === prefix);
  if (own) return `/${own} is used by the data plane itself. Choose a different path.`;
  const taken = (state.reservedPrefixes ?? []).find((prefix) => {
    const reserved = segmentsOf(`/${prefix}`);
    return reserved.length > 0 && reserved.every((segment, i) => segments[i] === segment);
  });
  return taken ? `/${taken} is already used by another service. Choose a different path.` : undefined;
};

/**
 * Exact before guessed, then longest: a literal `/bills/businessUnit/395/batch` authored for one
 * unit wins over the template that would also take it, and `/v2/bills/update-status` is never
 * shadowed by `/v2/bills`.
 */
export const matchRouteBinding = (state: IdentityState, method: string, path: string): RouteMatch | undefined => {
  const wanted = method.toUpperCase();
  const ranked = [...(state.routeBindings ?? [])].sort(
    (a, b) =>
      countParameters(a.path) - countParameters(b.path) ||
      segmentsOf(b.path).length - segmentsOf(a.path).length ||
      b.path.length - a.path.length,
  );

  for (const binding of ranked) {
    if (binding.method !== wanted) continue;
    const params = matchPath(binding.path, path);
    if (params) return { binding, params };
  }
  return undefined;
};

export const findRouteBinding = (state: IdentityState, method: string, path: string): RouteBinding | undefined =>
  matchRouteBinding(state, method, path)?.binding;

export const clauseHolds = (
  clause: DiscriminatorClause,
  payload: unknown,
  headers: Record<string, string> = {},
): boolean => {
  if (clause.kind === 'header-contains') {
    const actual = headers[clause.header.toLowerCase()];
    return typeof actual === 'string' && actual.toLowerCase().includes(clause.value.toLowerCase());
  }

  const value = getValue(payload, clause.path);
  if (clause.kind === 'field-present') return value !== undefined && value !== null;
  if (clause.kind === 'field-absent') return value === undefined || value === null;

  return value !== undefined && value !== null && String(value) === clause.value;
};

const ruleApplies = (rule: VendorRule, businessId: string, tenantId: string): boolean =>
  (rule.businessId === undefined || rule.businessId === businessId) &&
  (rule.tenantId === undefined || rule.tenantId === tenantId);

/**
 * The order a configuration is looked for, most specific first. A row is authored only where it
 * is needed: a vendor whose shape never varies has one `vendor:` row and nothing else.
 */
export const scopeProbeOrder = (context: { businessId?: string; tenantId?: string; vendorId?: string }): string[] =>
  [
    context.businessId && context.vendorId ? `business:${context.businessId}|vendor:${context.vendorId}` : undefined,
    context.businessId ? `business:${context.businessId}` : undefined,
    context.tenantId && context.vendorId ? `tenant:${context.tenantId}|vendor:${context.vendorId}` : undefined,
    context.tenantId ? `tenant:${context.tenantId}` : undefined,
    context.vendorId ? `vendor:${context.vendorId}` : undefined,
    'shared',
  ].filter((scope): scope is string => scope !== undefined);

/** The first authored scope that applies, or the shared one. */
export const resolveScope = (
  authored: string[],
  context: { businessId?: string; tenantId?: string; vendorId?: string },
): string => scopeProbeOrder(context).find((scope) => authored.includes(scope)) ?? 'shared';

/**
 * Which vendor sent this request.
 *
 * A key tied to one vendor answers immediately, and so does a route with a single candidate —
 * between them that is nearly all real traffic, and neither reads the payload. Only a business
 * running two vendors on one endpoint pays for discrimination.
 */
export const resolveVendor = (
  state: IdentityState,
  input: { routeId: string; businessId: string; tenantId: string; credential?: Credential },
  payload: unknown,
  headers: Record<string, string> = {},
): { vendorId: string; why: string } | { candidates: string[]; why: string } => {
  if (input.credential?.vendorId) {
    return { vendorId: input.credential.vendorId, why: 'the key is registered to this vendor' };
  }

  const applicable = (state.vendorRules ?? [])
    .filter((rule) => rule.routeId === input.routeId && ruleApplies(rule, input.businessId, input.tenantId))
    .sort((a, b) => a.priority - b.priority);

  const vendors = [...new Set(applicable.map((rule) => rule.vendorId))];
  if (vendors.length === 0) return { candidates: [], why: 'no vendor is configured for this route' };
  if (vendors.length === 1) return { vendorId: vendors[0], why: 'the only vendor configured for this route' };

  const matched = applicable.filter((rule) => rule.clauses.every((clause) => clauseHolds(clause, payload, headers)));
  if (matched.length === 0) return { candidates: vendors, why: 'the payload matched no vendor rule' };

  const best = matched[0];
  // a tie on priority between two different vendors is a configuration fault, not a coin toss
  const tied = matched.filter((rule) => rule.priority === best.priority && rule.vendorId !== best.vendorId);
  if (tied.length > 0) {
    return { candidates: [best.vendorId, ...tied.map((rule) => rule.vendorId)], why: 'two vendor rules tie' };
  }

  return { vendorId: best.vendorId, why: `matched ${best.clauses.length} clause(s)` };
};

/**
 * The scheme guarding a path, which is the default one until an author says otherwise. A scheme
 * named but missing is nothing, never the default: that would quietly accept a different header.
 */
export const schemeOf = (state: IdentityState, binding: RouteBinding): AuthScheme | undefined =>
  binding.authSchemeId ? state.authSchemes?.find((scheme) => scheme.id === binding.authSchemeId) : DEFAULT_AUTH_SCHEME;

/**
 * Which header the secret for this call arrives in.
 *
 * The door has to know this before it can hash anything, and it can: the path alone decides the
 * scheme, and reading a path costs nothing.
 */
export const keyHeaderFor = (state: IdentityState, method: string, path: string): string => {
  const match = matchRouteBinding(state, method, path);
  return (match && schemeOf(state, match.binding)?.keyHeader) || DEFAULT_AUTH_SCHEME.keyHeader;
};

/**
 * Which business is calling.
 *
 * A key issued to one business answers by itself. A key issued to a tenant does not, so the
 * scheme says where the unit is named — a segment of the URL, or a header — and the unit found
 * there must still sit under the tenant that key belongs to. Without that last check a tenant
 * token would reach every unit on the platform.
 */
const resolveUnit = (
  state: IdentityState,
  credential: Credential,
  scheme: AuthScheme,
  params: Record<string, string>,
  headers: Record<string, string>,
): { business: Business } | { reason: 'unknown-key' | 'unknown-unit' | 'unit-not-allowed'; detail: string } => {
  if (!scheme.unitFrom) {
    const business = businessOf(state, credential.businessId);
    return business ? { business } : { reason: 'unknown-key', detail: 'This api-key is not attached to a business.' };
  }

  const { source, name, matches } = scheme.unitFrom;
  const value = source === 'path' ? params[name] : headers[name.toLowerCase()];
  if (!value) {
    return {
      reason: 'unknown-unit',
      detail: `This endpoint expects the business unit in the ${source === 'path' ? 'path' : `${name} header`}, and none was sent.`,
    };
  }

  const business = (state.businesses ?? []).find((candidate) =>
    matches === 'externalUnitId' ? candidate.externalUnitId === value : candidate.id === value,
  );
  if (!business) return { reason: 'unknown-unit', detail: `No business unit is configured as ${value}.` };

  const owned = credential.businessId
    ? credential.businessId === business.id
    : credential.tenantId
      ? credential.tenantId === business.tenantId
      : false;
  if (!owned) return { reason: 'unit-not-allowed', detail: `This key may not send for business unit ${value}.` };

  return { business };
};

/** The whole door: key and path in, execution context out. No payload read unless it must be. */
export const resolveRequest = (
  state: IdentityState,
  request: { keyHash: string; method: string; path: string },
  payload: unknown = undefined,
  headers: Record<string, string> = {},
  authoredScopes: string[] = [],
): Resolution => {
  const credential = findCredential(state, request.keyHash);
  if (!credential) return { ok: false, reason: 'unknown-key', detail: 'This api-key is not recognised.' };
  if (credential.revokedAt) return { ok: false, reason: 'revoked-key', detail: 'This api-key has been revoked.' };

  const match = matchRouteBinding(state, request.method, request.path);
  if (!match) {
    return {
      ok: false,
      reason: 'unknown-path',
      detail: `No route is configured for ${request.method} ${request.path}.`,
    };
  }
  const { binding, params } = match;
  const scheme = schemeOf(state, binding);
  if (!scheme) return { ok: false, reason: 'no-scheme', detail: 'This endpoint is not configured correctly.' };
  const unit = resolveUnit(state, credential, scheme, params, headers);
  if ('reason' in unit) return { ok: false, reason: unit.reason, detail: unit.detail };
  const business = unit.business;

  /*
   * A tenant header is a claim, not a credential: it is checked against the key rather than
   * believed, so a caller cannot reach another tenant by changing a header.
   */
  if (scheme.tenantHeader) {
    const claimed = headers[scheme.tenantHeader.toLowerCase()];
    if (claimed && claimed !== business.tenantId) {
      return {
        ok: false,
        reason: 'tenant-mismatch',
        detail: `The ${scheme.tenantHeader} header says ${claimed}, but this key belongs to ${business.tenantId}.`,
      };
    }
  }

  if (credential.allowedRouteIds?.length && !credential.allowedRouteIds.includes(binding.routeId)) {
    return { ok: false, reason: 'route-not-allowed', detail: 'This api-key may not use this endpoint.' };
  }

  const found = resolveVendor(
    state,
    { routeId: binding.routeId, businessId: business.id, tenantId: business.tenantId, credential },
    payload,
    headers,
  );
  if ('candidates' in found) {
    return found.candidates.length > 1
      ? {
          ok: false,
          reason: 'ambiguous-vendor',
          detail: found.why,
          routeId: binding.routeId,
          candidates: found.candidates,
        }
      : { ok: false, reason: 'no-vendor', detail: found.why, routeId: binding.routeId };
  }

  return {
    ok: true,
    businessId: business.id,
    tenantId: business.tenantId,
    routeId: binding.routeId,
    vendorId: found.vendorId,
    params,
    scope: resolveScope(authoredScopes, {
      businessId: business.id,
      tenantId: business.tenantId,
      vendorId: found.vendorId,
    }),
    why: found.why,
  };
};

/* ── authoring ───────────────────────────────────────────────────────────────────────────── */

const slug = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export const addBusiness = <T extends IdentityState>(
  state: T,
  tenantId: string,
  name: string,
  externalId?: string,
): T => {
  const id = externalId?.trim() || slug(name);
  if (!id || state.businesses?.some((business) => business.id === id)) return state;

  return { ...state, businesses: [...(state.businesses ?? []), { id, name: name.trim(), tenantId }] };
};

/** The key itself is never stored: the caller shows it once and keeps only this hash. */
export const addCredential = <T extends IdentityState>(
  state: T,
  credential: Omit<Credential, 'id' | 'createdAt'> & { createdAt?: string },
): T => {
  /*
   * A key belongs either to one business or to a whole tenant. The tenant form is for vendors
   * that name the unit on every call, so one token covers the units under it and no others.
   */
  const owner = credential.businessId
    ? state.businesses?.some((business) => business.id === credential.businessId)
    : credential.tenantId
      ? state.businesses?.some((business) => business.tenantId === credential.tenantId)
      : false;
  if (!owner) return state;
  if (state.credentials?.some((existing) => existing.keyHash === credential.keyHash)) return state;

  return {
    ...state,
    credentials: [
      ...(state.credentials ?? []),
      {
        ...credential,
        id: `cred-${credential.keyHash.slice(0, 12)}`,
        createdAt: credential.createdAt ?? new Date().toISOString(),
      },
    ],
  };
};

/**
 * Revoked, not deleted. A deleted key leaves no trace of what was once authorised, and the
 * audit question "what could this key reach" outlives the key.
 */
export const revokeCredential = <T extends IdentityState>(state: T, credentialId: string, at?: string): T => ({
  ...state,
  credentials: (state.credentials ?? []).map((credential) =>
    credential.id === credentialId
      ? { ...credential, revokedAt: credential.revokedAt ?? at ?? new Date().toISOString() }
      : credential,
  ),
});

/**
 * Authoring a scheme is how a vendor's own habits are taken in: the header their token arrives
 * under, the header naming their tenant, and where they put the unit. A scheme in use is
 * replaced in place, so correcting a header name does not orphan the paths that point at it.
 */
/**
 * The id this business is known by on the vendor's side.
 *
 * Only needed when the two do not agree. A scheme that takes the unit out of a URL matches on
 * this, so a vendor addressing `/businessUnit/395/` reaches whichever business was told that 395
 * is its number here — without anyone renaming anything.
 */
export const setExternalUnitId = <T extends IdentityState>(state: T, businessId: string, external: string): T => ({
  ...state,
  businesses: (state.businesses ?? []).map((business) =>
    business.id === businessId
      ? external.trim()
        ? { ...business, externalUnitId: external.trim() }
        : { ...business, externalUnitId: undefined }
      : business,
  ),
});

export const saveAuthScheme = <T extends IdentityState>(state: T, scheme: AuthScheme): T => ({
  ...state,
  authSchemes: [...(state.authSchemes ?? []).filter((existing) => existing.id !== scheme.id), scheme],
});

/** Refused while a path still points at it, because that path would silently change its rules. */
export const removeAuthScheme = <T extends IdentityState>(state: T, schemeId: string): T =>
  state.routeBindings?.some((binding) => binding.authSchemeId === schemeId)
    ? state
    : { ...state, authSchemes: (state.authSchemes ?? []).filter((scheme) => scheme.id !== schemeId) };

/** Why this scheme cannot be saved, in the author's words, or nothing when it is fine. */
export const authSchemeFault = (scheme: AuthScheme, state?: IdentityState): string | undefined => {
  const header = (value: string | undefined): boolean => !!value && /^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/.test(value);
  if (!scheme.name?.trim()) return 'Give the scheme a name.';
  if (!header(scheme.keyHeader)) return 'Name the header the secret arrives in, such as api-key.';
  if (scheme.tenantHeader !== undefined && scheme.tenantHeader !== '' && !header(scheme.tenantHeader)) {
    return 'The tenant header is not a usable header name.';
  }
  if (scheme.tenantHeader && scheme.tenantHeader.toLowerCase() === scheme.keyHeader.toLowerCase()) {
    return 'The tenant and the secret cannot arrive in the same header.';
  }
  if (scheme.fallbackKeyHeader !== undefined && scheme.fallbackKeyHeader !== '') {
    if (!header(scheme.fallbackKeyHeader)) return 'The second secret header is not a usable header name.';
    const fallback = scheme.fallbackKeyHeader.toLowerCase();
    if (fallback === scheme.keyHeader.toLowerCase()) return 'The second secret header repeats the first.';
    if (fallback === scheme.tenantHeader?.toLowerCase()) {
      return 'The tenant and the secret cannot arrive in the same header.';
    }
  }
  for (const [kind, status] of Object.entries(scheme.refusalStatus ?? {})) {
    if (!(REFUSAL_KINDS as readonly string[]).includes(kind)) return `"${kind}" is not a kind of refusal.`;
    if (status !== undefined && !(Number.isInteger(status) && status >= 400 && status <= 499)) {
      return 'A refusal status must be a 4xx code, such as 400, 401 or 403.';
    }
  }
  if (scheme.unitFrom) {
    if (scheme.unitFrom.source !== 'path' && scheme.unitFrom.source !== 'header') {
      return 'The business unit must come from the path or a header.';
    }
    if (scheme.unitFrom.matches !== 'id' && scheme.unitFrom.matches !== 'externalUnitId') {
      return "The business unit must be matched on the business's id or its external unit id.";
    }
    if (!scheme.unitFrom.name?.trim()) return 'Say where the business unit is named.';
    if (scheme.unitFrom.source === 'header' && !header(scheme.unitFrom.name)) {
      return 'The unit header is not a usable header name.';
    }
  }
  if (state && scheme.unitFrom?.source === 'path') {
    const bound = (state.routeBindings ?? []).filter((binding) => binding.authSchemeId === scheme.id);
    const missing = bound.find((binding) => !binding.path.includes(`{${scheme.unitFrom!.name}}`));
    if (missing) return `${missing.method} ${missing.path} has no {${scheme.unitFrom.name}} in it.`;
  }
  return undefined;
};

export const saveVendorRule = <T extends IdentityState>(state: T, rule: VendorRule): T => ({
  ...state,
  vendorRules: [...(state.vendorRules ?? []).filter((existing) => existing.id !== rule.id), rule],
});

export const removeVendorRule = <T extends IdentityState>(state: T, ruleId: string): T => ({
  ...state,
  vendorRules: (state.vendorRules ?? []).filter((rule) => rule.id !== ruleId),
});

/**
 * How a set of rules behaves across every captured payload. This is what makes a discriminator
 * trustworthy before it is published: a rule that matches everything, or nothing, is visible.
 */
export const ruleCoverage = (
  state: IdentityState,
  routeId: string,
  samples: { name: string; payload: unknown }[],
  context: { businessId: string; tenantId: string },
): { name: string; vendorId?: string; problem?: string }[] =>
  samples.map((sample) => {
    const found = resolveVendor(state, { routeId, ...context }, sample.payload);

    return 'vendorId' in found
      ? { name: sample.name, vendorId: found.vendorId }
      : { name: sample.name, problem: found.why };
  });

export const renameBusiness = <T extends IdentityState>(state: T, businessId: string, name: string): T => {
  const next = name.trim();
  if (!next) throw new Error('Business name cannot be empty.');
  const business = state.businesses?.find((item) => item.id === businessId);
  if (!business) throw new Error('Business not found.');
  if (
    (state.businesses ?? []).some(
      (item) =>
        item.id !== businessId && item.tenantId === business.tenantId && item.name.toLowerCase() === next.toLowerCase(),
    )
  )
    throw new Error('This tenant already has a business with that name.');

  return {
    ...state,
    businesses: (state.businesses ?? []).map((item) => (item.id === businessId ? { ...item, name: next } : item)),
  };
};

/**
 * A copy of a business under the same tenant.
 *
 * Its credentials are deliberately not copied: a key is what identifies one business at the
 * door, so a copy that carried them would be indistinguishable from the original and traffic
 * meant for one would resolve to the other.
 */
export const duplicateBusiness = <T extends IdentityState>(state: T, businessId: string, name: string): T => {
  const source = state.businesses?.find((item) => item.id === businessId);
  if (!source) throw new Error('Business not found.');
  const id = slug(name);
  if (!id || state.businesses?.some((item) => item.id === id)) throw new Error('That name is taken, or unusable.');

  return {
    ...state,
    businesses: [
      ...(state.businesses ?? []),
      { ...structuredClone(source), id, name: name.trim(), externalUnitId: undefined },
    ],
  };
};
